import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { drivingRoute, walkingRoute } from "../../services/amapService";
import "./styles.css";

const START_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="32" height="32"><path d="M528 32C325.056 32 160 196.8 160 399.36c0 75.2 22.656 147.584 65.024 208.48 2.112 3.648 4.256 7.168 6.784 10.592l268.608 353.472c7.296 8.096 17.088 12.576 27.584 12.576 10.368 0 20.224-4.512 28.768-14.08l267.36-352c2.624-3.52 4.896-7.36 6.112-9.6A364.864 364.864 0 0 0 896 399.36C896 196.8 730.912 32 528 32z m0 498.72a131.52 131.52 0 0 1-131.456-131.232 131.488 131.488 0 0 1 262.88 0 131.52 131.52 0 0 1-131.424 131.2z" fill="#22c55e"/></svg>`;
const END_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="32" height="32"><path d="M528 32C325.056 32 160 196.8 160 399.36c0 75.2 22.656 147.584 65.024 208.48 2.112 3.648 4.256 7.168 6.784 10.592l268.608 353.472c7.296 8.096 17.088 12.576 27.584 12.576 10.368 0 20.224-4.512 28.768-14.08l267.36-352c2.624-3.52 4.896-7.36 6.112-9.6A364.864 364.864 0 0 0 896 399.36C896 196.8 730.912 32 528 32z m0 498.72a131.52 131.52 0 0 1-131.456-131.232 131.488 131.488 0 0 1 262.88 0 131.52 131.52 0 0 1-131.424 131.2z" fill="#ef4444"/></svg>`;

const TRAFFIC_COLORS = {
  "畅通": "#22c55e",
  "缓行": "#f59e0b",
  "拥堵": "#ef4444",
  "严重拥堵": "#b91c1c",
  "未知": "#025bff",
  "公交/地铁": "#025bff",
  "步行": "#9333ea",
};

function parsePolyline(polylineStr) {
  if (!polylineStr) return [];
  return polylineStr.split(";").map((p) => {
    const [lng, lat] = p.split(",").map(Number);
    return [lng, lat];
  });
}

function getTrafficSegments(steps) {
  const segments = [];
  steps.forEach((step) => {
    if (step.tmcs && step.tmcs.length > 0) {
      step.tmcs.forEach((tmc) => {
        const pts = parsePolyline(tmc.polyline);
        if (pts.length > 1) {
          segments.push({ path: pts, status: tmc.status || "未知" });
        }
      });
    } else {
      const pts = parsePolyline(step.polyline);
      if (pts.length > 1) {
        segments.push({ path: pts, status: "未知" });
      }
    }
  });
  return segments;
}

function getTransitMapSegments(segments) {
  const mapSegments = [];
  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    (segment?.bus?.buslines || []).forEach((line) => {
      const pts = parsePolyline(line?.polyline);
      if (pts.length > 1) {
        mapSegments.push({ path: pts, status: "公交/地铁" });
      }
    });
    (segment?.walking?.steps || []).forEach((step) => {
      const pts = parsePolyline(step?.polyline);
      if (pts.length > 1) {
        mapSegments.push({ path: pts, status: "步行" });
      }
    });
  });
  return mapSegments;
}

function formatDistance(meters) {
  const m = Number(meters);
  if (isNaN(m)) return meters;
  return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${m}m`;
}

function formatDuration(seconds) {
  const s = Number(seconds);
  if (isNaN(s)) return seconds;
  if (s < 60) return `${s}秒`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}分钟`;
  const hr = Math.floor(min / 60);
  const remainMin = min % 60;
  return remainMin > 0 ? `${hr}小时${remainMin}分钟` : `${hr}小时`;
}

function parseLngLatPair(value) {
  if (typeof value !== "string") return null;
  const [lng, lat] = value.split(",").map(Number);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
}

function isRouteUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

function getRouteObject(value) {
  if (!value || typeof value !== "object") return null;
  if (value.paths || value.transits) return value;
  if (value.route && typeof value.route === "object") return getRouteObject(value.route);
  return null;
}

async function getFallbackRoute(routeData) {
  const origin = routeData?.origin;
  const destination = routeData?.destination;
  if (!origin || !destination) return null;

  if (routeData?.tool === "maps_direction_walking") {
    return walkingRoute(origin, destination);
  }

  if (routeData?.tool === "maps_direction_driving" || routeData?.tool === "maps_direction") {
    return drivingRoute(origin, destination);
  }

  return null;
}

function fitRouteBounds(map, origin, destination) {
  // 用起终点+所有 polyline 覆盖物手动算 bounds，比 setFitView 更可控
  const overlays = map.getAllOverlays("polyline");
  const allLngs = [];
  const allLats = [];

  overlays.forEach((ol) => {
    const path = ol.getPath();
    if (path) {
      path.forEach((p) => {
        allLngs.push(p.lng ?? p[0]);
        allLats.push(p.lat ?? p[1]);
      });
    }
  });

  // 加入起终点
  const originPair = parseLngLatPair(origin);
  const destinationPair = parseLngLatPair(destination);
  if (originPair) {
    allLngs.push(originPair[0]);
    allLats.push(originPair[1]);
  }
  if (destinationPair) {
    allLngs.push(destinationPair[0]);
    allLats.push(destinationPair[1]);
  }

  if (allLngs.length < 2) return;

  const lngSpan = Math.max(...allLngs) - Math.min(...allLngs);
  const latSpan = Math.max(...allLats) - Math.min(...allLats);
  // 按路线跨度的 15% 做 padding，至少 0.005
  const lngPad = Math.max(lngSpan * 0.15, 0.005);
  const latPad = Math.max(latSpan * 0.15, 0.005);

  map.setBounds(
    new window.AMap.Bounds(
      [Math.min(...allLngs) - lngPad, Math.min(...allLats) - latPad],
      [Math.max(...allLngs) + lngPad, Math.max(...allLats) + latPad]
    )
  );
}

function addRouteToMap(map, segments, origin, destination, interactive) {
  segments.forEach((seg) => {
    const color = TRAFFIC_COLORS[seg.status] || TRAFFIC_COLORS["未知"];
    const polyline = new window.AMap.Polyline({
      path: seg.path,
      strokeColor: color,
      strokeWeight: interactive ? 7 : 5,
      strokeOpacity: 0.9,
      lineJoin: "round",
      lineCap: "round",
    });
    map.add(polyline);
  });

  const originPair = parseLngLatPair(origin);
  const destinationPair = parseLngLatPair(destination);
  if (!originPair || !destinationPair) return;
  const [oLng, oLat] = originPair;
  const [dLng, dLat] = destinationPair;

  const startEl = document.createElement("div");
  startEl.className = "route-marker route-marker--start";
  startEl.innerHTML = `<div class="route-marker-label">起点</div><div class="route-marker-icon">${START_SVG}</div>`;
  map.add(
    new window.AMap.Marker({
      position: [oLng, oLat],
      content: startEl,
      anchor: "bottom-center",
      offset: new window.AMap.Pixel(0, 0),
    })
  );

  const endEl = document.createElement("div");
  endEl.className = "route-marker route-marker--end";
  endEl.innerHTML = `<div class="route-marker-label">终点</div><div class="route-marker-icon">${END_SVG}</div>`;
  map.add(
    new window.AMap.Marker({
      position: [dLng, dLat],
      content: endEl,
      anchor: "bottom-center",
      offset: new window.AMap.Pixel(0, 0),
    })
  );
}

/* ===== 小地图 ===== */
const RouteMiniMap = React.memo(({ segments, origin, destination, onClick }) => {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);

  const initMap = useCallback(() => {
    if (!containerRef.current || !window.AMap || mapRef.current) return;

    const originPair = parseLngLatPair(origin);
    if (!originPair) return;
    const [oLng, oLat] = originPair;
    const map = new window.AMap.Map(containerRef.current, {
      zoom: 12,
      center: [oLng, oLat],
      mapStyle: "amap://styles/normal",
      dragEnable: false,
      zoomEnable: false,
      touchZoom: false,
      doubleClickZoom: false,
      scrollWheel: false,
      keyboardEnable: false,
      showIndoorMap: false,
    });

    mapRef.current = map;

    map.on("complete", () => {
      addRouteToMap(map, segments, origin, destination, false);
      fitRouteBounds(map, origin, destination);
      setReady(true);
    });
  }, [segments, origin, destination]);

  const destroyMap = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.destroy();
      mapRef.current = null;
      setReady(false);
    }
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) initMap();
        else destroyMap();
      },
      { rootMargin: "800px 0px" }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      destroyMap();
    };
  }, [initMap, destroyMap]);

  return (
    <div className="route-card-map" ref={wrapperRef}>
      {!ready && (
        <div className="route-card-map-placeholder">
          <div className="route-card-map-placeholder-pulse" />
        </div>
      )}
      <div className="route-card-map-inner" ref={containerRef} />
      <div className="route-card-map-touch" onClick={onClick} />
      <div className="route-card-map-expand">点击查看大地图</div>
    </div>
  );
});

/* ===== 全屏地图 ===== */
const RouteFullMap = ({ segments, origin, destination, onClose }) => {
  const mapContainerRef = useRef(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!mapContainerRef.current || !window.AMap) return;

    const originPair = parseLngLatPair(origin);
    if (!originPair) return;
    const [oLng, oLat] = originPair;
    const map = new window.AMap.Map(mapContainerRef.current, {
      zoom: 12,
      center: [oLng, oLat],
      mapStyle: "amap://styles/normal",
    });

    map.on("complete", () => {
      addRouteToMap(map, segments, origin, destination, true);
      fitRouteBounds(map, origin, destination);
    });

    return () => map.destroy();
  }, [segments, origin, destination]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  return (
    <div
      className={`route-fullmap-overlay${closing ? " route-fullmap-closing" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`route-fullmap-container${closing ? " route-fullmap-container-closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="route-fullmap-header">
          <span className="route-fullmap-title">路线规划</span>
          <button type="button" className="route-fullmap-close" onClick={handleClose}>
            ✕
          </button>
        </div>
        <div className="route-fullmap-body" ref={mapContainerRef} />
      </div>
    </div>
  );
};

/* ===== 主组件 ===== */
const RouteCard = ({ routeData }) => {
  const [showSteps, setShowSteps] = useState(false);
  const [showFullMap, setShowFullMap] = useState(false);
  const [remoteRoute, setRemoteRoute] = useState(null);
  const [routeLoadStatus, setRouteLoadStatus] = useState("idle");

  const routeUrl = isRouteUrl(routeData?.route) ? routeData.route : "";
  const routeOrigin = routeData?.origin;
  const routeDestination = routeData?.destination;
  const routeTool = routeData?.tool;

  useEffect(() => {
    if (!routeUrl) {
      setRemoteRoute(null);
      setRouteLoadStatus("idle");
      return;
    }

    const controller = new AbortController();
    setRouteLoadStatus("loading");
    setRemoteRoute(null);

    const loadRoute = async () => {
      try {
        const res = await fetch(routeUrl, { signal: controller.signal });
        if (!res.ok) {
          throw new Error(`Route json request failed: ${res.status}`);
        }
        const data = await res.json();
        if (controller.signal.aborted) return;
        setRemoteRoute(data);
        setRouteLoadStatus("success");
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to load route json:", err);
        try {
          const fallbackRoute = await getFallbackRoute({
            origin: routeOrigin,
            destination: routeDestination,
            tool: routeTool,
          });
          if (controller.signal.aborted) return;
          if (fallbackRoute) {
            setRemoteRoute(fallbackRoute);
            setRouteLoadStatus("success");
            return;
          }
        } catch (fallbackErr) {
          if (!controller.signal.aborted) {
            console.error("Failed to load fallback route:", fallbackErr);
          }
        }
        if (!controller.signal.aborted) {
          setRemoteRoute(null);
          setRouteLoadStatus("error");
        }
      }
    };

    loadRoute();

    return () => controller.abort();
  }, [routeUrl, routeOrigin, routeDestination, routeTool]);

  const route = useMemo(() => {
    const localRoute = getRouteObject(routeData?.route);
    const fetchedRoute = getRouteObject(remoteRoute);
    return fetchedRoute || localRoute;
  }, [routeData?.route, remoteRoute]);

  const routeView = useMemo(() => {
    if (!route) return null;

    const origin = route.origin || routeData.origin;
    const destination = route.destination || routeData.destination;
    const path = route?.paths?.[0];
    const transit = route?.transits?.[0];
    const isDriving = !!path;
    const isTransit = !isDriving && !!transit;

    if (!isDriving && !isTransit) return null;

    let steps = [];
    let mapSegments = [];
    let distance = "";
    let duration = "";
    let taxiCost = route.taxi_cost;
    let strategy = "";
    let trafficLights = "";

    if (isDriving) {
      steps = path.steps || [];
      mapSegments = getTrafficSegments(steps);
      distance = formatDistance(path.distance);
      duration = formatDuration(path.duration);
      strategy = path.strategy;
      trafficLights = path.traffic_lights;
    } else {
      const transitSegments = transit.segments || [];
      mapSegments = getTransitMapSegments(transitSegments);
      steps = transitSegments.map((seg) => {
        const line = seg?.bus?.buslines?.[0];
        if (line) {
          const dep = line?.departure_stop?.name || "起点站";
          const arr = line?.arrival_stop?.name || "终点站";
          return {
            instruction: `${line.name} (${dep} -> ${arr})`,
            distance: line.distance,
            duration: line.duration,
            road: "公交/地铁",
          };
        }
        const walk = seg?.walking;
        return {
          instruction: walk?.steps?.[0]?.instruction || "步行换乘",
          distance: walk?.distance,
          duration: walk?.duration,
          road: "步行",
        };
      });
      distance = formatDistance(transit.distance || route.distance);
      duration = formatDuration(transit.duration);
      strategy = "公交/地铁换乘";
      taxiCost = transit.cost || route.taxi_cost;
    }

    return {
      origin,
      destination,
      steps,
      mapSegments,
      distance,
      duration,
      taxiCost,
      strategy,
      trafficLights,
    };
  }, [route, routeData?.origin, routeData?.destination]);

  const toggleSteps = useCallback(() => {
    setShowSteps((prev) => !prev);
  }, []);

  const openFullMap = useCallback(() => {
    setShowFullMap(true);
  }, []);

  const closeFullMap = useCallback(() => {
    setShowFullMap(false);
  }, []);

  if (!routeView) {
    if (routeLoadStatus === "loading") {
      return (
        <div className="route-card">
          <div className="route-card-summary">
            <div className="route-card-title">
              <span className="route-card-title-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#025bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  <circle cx="12" cy="9" r="2.5" />
                </svg>
              </span>
              路线规划加载中...
            </div>
          </div>
          <div className="route-card-map">
            <div className="route-card-map-placeholder">
              <div className="route-card-map-placeholder-pulse" />
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const { origin, destination, steps, mapSegments, distance, duration, taxiCost, strategy, trafficLights } = routeView;

  return (
    <>
      <div className="route-card">
        <div className="route-card-summary">
          <div className="route-card-title">
            <span className="route-card-title-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#025bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
            </span>
            路线规划
          </div>
          <div className="route-card-meta">
            <div className="route-card-meta-item">
              <span className="route-card-meta-label">总距离</span>
              <span className="route-card-meta-value">{distance}</span>
            </div>
            <div className="route-card-meta-item">
              <span className="route-card-meta-label">预计用时</span>
              <span className="route-card-meta-value route-card-meta-value--highlight">
                {duration}
              </span>
            </div>
            {taxiCost && (
              <div className="route-card-meta-item">
                <span className="route-card-meta-label">打车费用</span>
                <span className="route-card-meta-value">¥{taxiCost}</span>
              </div>
            )}
            {trafficLights && (
              <div className="route-card-meta-item">
                <span className="route-card-meta-label">红绿灯</span>
                <span className="route-card-meta-value">{trafficLights}个</span>
              </div>
            )}
          </div>
          {strategy && <span className="route-card-strategy">{strategy}</span>}
        </div>

        {origin && destination && mapSegments.length > 0 && (
          <RouteMiniMap
            segments={mapSegments}
            origin={origin}
            destination={destination}
            onClick={openFullMap}
          />
        )}

        {steps.length > 0 && (
          <div className="route-card-steps">
            <button
              className="route-card-steps-toggle"
              onClick={toggleSteps}
            >
              {showSteps ? "收起导航步骤" : `展开导航步骤 (${steps.length}步)`}
              <span className={`route-card-steps-arrow${showSteps ? " route-card-steps-arrow--open" : ""}`}>
                ▼
              </span>
            </button>
            {showSteps && (
              <ul className="route-card-steps-list">
                {steps.map((step, idx) => (
                  <li key={idx} className="route-card-step-item">
                    <div className="route-card-step-dot" />
                    <div className="route-card-step-info">
                      <div className="route-card-step-instruction">
                        {step.instruction}
                      </div>
                      <div className="route-card-step-detail">
                        {formatDistance(step.distance)}
                        {step.duration ? ` · ${formatDuration(step.duration)}` : ""}
                        {step.road ? ` · ${step.road}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {showFullMap &&
        origin &&
        destination &&
        mapSegments.length > 0 &&
        typeof document !== "undefined" &&
        createPortal(
          <RouteFullMap
            segments={mapSegments}
            origin={origin}
            destination={destination}
            onClose={closeFullMap}
          />,
          document.body
        )}
    </>
  );
};

export default RouteCard;
